import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import Box from '@material-ui/core/Box';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import DrawIcon from '@material-ui/icons/CreateOutlined';
import TrashIcon from '@material-ui/icons/DeleteOutline';
import FreehandIcon from '@material-ui/icons/Gesture';
import { DragCircleMode } from 'mapbox-gl-draw-circle';
import FreehandMode from 'mapbox-gl-draw-freehand-mode';
import RectangleMode from 'mapbox-gl-draw-rectangle-mode';
import React, { useEffect, useState } from 'react';

import { CircleIcon, LineIcon, PolygonIcon, RectangleIcon } from './SvgIcons';

interface Control {
  icon: (props: any) => JSX.Element;
  mode: string;
}

const CONTROLS: Control[] = [
  {
    icon: FreehandIcon,
    mode: 'draw_freehand'
  },
  {
    icon: LineIcon,
    mode: 'draw_line_string'
  },
  {
    icon: RectangleIcon,
    mode: 'draw_rectangle'
  },
  {
    icon: PolygonIcon,
    mode: 'draw_polygon'
  },
  {
    icon: CircleIcon,
    mode: 'draw_circle'
  }
];

export interface DrawControlProps {
  container: HTMLDivElement;
  map: mapboxgl.Map;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    controlGroup: {
      maxHeight: 30,
      overflowY: 'hidden',
      transition: 'max-height 0.25s ease-out'
    },
    expand: {
      maxHeight: 210
    },
    iconCentered: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  })
);

const DrawControl: React.FC<DrawControlProps> = ({ container, map }) => {
  const [draw] = useState(
    new MapboxDraw({
      displayControlsDefault: false,
      modes: {
        ...MapboxDraw.modes,
        draw_circle: DragCircleMode,
        draw_freehand: FreehandMode,
        draw_rectangle: RectangleMode
      },
      userProperties: true
    })
  );

  const classes = useStyles();

  useEffect(() => {
    container.classList.add(classes.controlGroup);

    return () => {
      container.classList.remove(classes.controlGroup);
    };
  }, [container, classes]);

  useEffect(() => {
    map.addControl(draw);

    return () => {
      map.removeControl(draw);
    };
  }, [map, draw]);

  const toggleExpand = () => {
    container.classList.toggle(classes.expand);
  };

  const changeMode = (mode: string) => {
    draw.changeMode(mode);
  };

  const trash = () => {
    draw.trash();
  };

  return (
    <React.Fragment>
      <button onClick={toggleExpand}>
        <Box className={classes.iconCentered}>
          <DrawIcon fontSize="small" />
        </Box>
      </button>

      {CONTROLS.map(({ icon: Icon, mode }, index) => (
        <button key={index} onClick={changeMode.bind(null, mode)}>
          {Icon && (
            <Box className={classes.iconCentered}>
              <Icon fontSize="small" />
            </Box>
          )}
        </button>
      ))}

      <button onClick={trash}>
        <Box className={classes.iconCentered}>
          <TrashIcon fontSize="small" />
        </Box>
      </button>
    </React.Fragment>
  );
};

export default DrawControl;
